import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, Save, RotateCcw, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-Seite zum Editieren der Push-Notification-Texte, die der
 * Scheduler verwendet (Termin-Erinnerungen 60 min / 24 h, Inaktivität,
 * Smart-Coach-Motivation).
 *
 * Vor 05/2026 lagen diese Strings hardcoded in `scheduler.py` —
 * jetzt werden sie aus der DB-Collection `push_notification_configs`
 * geladen, mit Default-Fallback.
 */
const PushConfigsPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [drafts, setDrafts] = useState({});

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/push-configs`,
        { headers },
      );
      setConfigs(data || []);
      // Drafts initialisieren
      const d = {};
      (data || []).forEach((c) => {
        const numeric = {};
        (c.numeric_fields || []).forEach((nf) => { numeric[nf.key] = nf.value; });
        d[c.notification_type] = {
          title: c.title,
          body: c.body,
          enabled: c.enabled,
          numeric,
        };
      });
      setDrafts(d);
    } catch (e) {
      toast.error(`Konfigurationen konnten nicht geladen werden: ${e.message}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = (type, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [type]: { ...prev[type], [field]: value },
    }));
  };

  const handleNumericChange = (type, key, value) => {
    setDrafts((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        numeric: { ...(prev[type]?.numeric || {}), [key]: value },
      },
    }));
  };

  const handleSave = async (type) => {
    setSaving((s) => ({ ...s, [type]: true }));
    try {
      const draft = drafts[type];
      const numericPayload = {};
      Object.entries(draft.numeric || {}).forEach(([k, v]) => {
        const n = parseInt(v, 10);
        if (!Number.isNaN(n)) numericPayload[k] = n;
      });
      await axios.put(
        `${BACKEND_URL}/api/admin/push-configs/${type}`,
        {
          title: draft.title,
          body: draft.body,
          enabled: draft.enabled,
          numeric: numericPayload,
        },
        { headers },
      );
      toast.success('Gespeichert ✓');
      await load();
    } catch (e) {
      toast.error(`Speichern fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving((s) => ({ ...s, [type]: false }));
    }
  };

  const handleReset = async (type) => {
    if (!window.confirm('Auf Default-Text zurücksetzen?')) return;
    setSaving((s) => ({ ...s, [type]: true }));
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/push-configs/${type}/reset`,
        {},
        { headers },
      );
      toast.success('Zurückgesetzt');
      await load();
    } catch (e) {
      toast.error(`Reset fehlgeschlagen: ${e.message}`);
    } finally {
      setSaving((s) => ({ ...s, [type]: false }));
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {!embedded && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/communication')}
            data-testid="push-configs-back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Zurück
          </Button>
          )}
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-yellow-500" />
            <h1 className="text-2xl font-bold">Push-Benachrichtigungen — Texte</h1>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          data-testid="push-configs-reload-button"
        >
          <RefreshCw className="h-4 w-4 mr-1" /> Aktualisieren
        </Button>
      </div>

      <p className="text-sm text-gray-500 mb-6">
        Die folgenden Texte werden automatisch vom Scheduler verschickt
        (Termin-Erinnerungen, Inaktivitäts-Push, Smart-Coach-Motivation).
        Variablen wie <code className="px-1 bg-gray-100 rounded">{'{service_name}'}</code> werden
        beim Senden ersetzt.
      </p>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Lade...</div>
      ) : (
        <div className="space-y-4">
          {configs.map((cfg) => {
            const draft = drafts[cfg.notification_type] || {};
            const numericDirty = (cfg.numeric_fields || []).some(
              (nf) => String(draft.numeric?.[nf.key] ?? '') !== String(nf.value),
            );
            const isDirty =
              draft.title !== cfg.title ||
              draft.body !== cfg.body ||
              draft.enabled !== cfg.enabled ||
              numericDirty;
            const isDefault =
              cfg.title === cfg.title_default && cfg.body === cfg.body_default;

            return (
              <Card
                key={cfg.notification_type}
                data-testid={`push-config-card-${cfg.notification_type}`}
              >
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <div>
                    <CardTitle className="text-base">{cfg.label}</CardTitle>
                    <p className="text-xs text-gray-400 mt-1">
                      Type: <code>{cfg.notification_type}</code>
                      {isDefault && (
                        <span className="ml-2 px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                          Default
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Aktiv</Label>
                    <Switch
                      checked={!!draft.enabled}
                      onCheckedChange={(v) =>
                        handleChange(cfg.notification_type, 'enabled', v)
                      }
                      data-testid={`push-config-enabled-${cfg.notification_type}`}
                    />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs">Titel</Label>
                    <Input
                      value={draft.title || ''}
                      onChange={(e) =>
                        handleChange(cfg.notification_type, 'title', e.target.value)
                      }
                      placeholder={cfg.title_default}
                      data-testid={`push-config-title-${cfg.notification_type}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Nachricht (Body)</Label>
                    <Textarea
                      value={draft.body || ''}
                      rows={2}
                      onChange={(e) =>
                        handleChange(cfg.notification_type, 'body', e.target.value)
                      }
                      placeholder={cfg.body_default}
                      data-testid={`push-config-body-${cfg.notification_type}`}
                    />
                    {cfg.variables?.length > 0 && (
                      <p className="text-xs text-gray-400 mt-1">
                        Verfügbare Variablen:{' '}
                        {cfg.variables.map((v) => (
                          <code
                            key={v}
                            className="ml-1 px-1 bg-gray-100 rounded text-xs"
                          >
                            {`{${v}}`}
                          </code>
                        ))}
                      </p>
                    )}
                  </div>
                  {(cfg.numeric_fields || []).length > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      {cfg.numeric_fields.map((nf) => (
                        <div key={nf.key}>
                          <Label className="text-xs">{nf.label}</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min={nf.min}
                              max={nf.max}
                              value={draft.numeric?.[nf.key] ?? ''}
                              onChange={(e) =>
                                handleNumericChange(
                                  cfg.notification_type,
                                  nf.key,
                                  e.target.value,
                                )
                              }
                              data-testid={`push-config-numeric-${cfg.notification_type}-${nf.key}`}
                            />
                            {nf.unit && (
                              <span className="text-xs text-gray-400 whitespace-nowrap">
                                {nf.unit}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-end gap-2 pt-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!!saving[cfg.notification_type] || isDefault}
                      onClick={() => handleReset(cfg.notification_type)}
                      data-testid={`push-config-reset-${cfg.notification_type}`}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Reset
                    </Button>
                    <Button
                      size="sm"
                      disabled={!!saving[cfg.notification_type] || !isDirty}
                      onClick={() => handleSave(cfg.notification_type)}
                      data-testid={`push-config-save-${cfg.notification_type}`}
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {saving[cfg.notification_type] ? 'Speichere...' : 'Speichern'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PushConfigsPage;
